import { useEffect, useState } from "react";
import { Header, PhoneRing } from "./ui/bits.js";
import { InstallHint } from "./ui/InstallHint.js";
import { Cart } from "./ui/Cart.js";
import { Checkout } from "./ui/Checkout.js";
import { Shop } from "./ui/Shop.js";
import { Track } from "./ui/Track.js";
import { errorText, useLang } from "./lib/i18n.js";
import { useApp } from "./lib/store.js";

export function App() {
  const { state, actions } = useApp();
  const { lang, t, toggle } = useLang();
  const [offline, setOffline] = useState(
    typeof navigator === "undefined" ? false : navigator.onLine === false,
  );
  const [askCode, setAskCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState("");

  function chooseBusiness() {
    if (state.wholesaleCode) {
      actions.setChannel("wholesale");
      return;
    }
    setCodeDraft("");
    setAskCode(true);
  }

  function submitCode() {
    const code = codeDraft.trim();
    if (!code) return;
    actions.setWholesaleCode(code);
    actions.setChannel("wholesale");
    setAskCode(false);
  }

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // a new screen starts at the top, not wherever the shelf was scrolled to
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [state.screen]);

  const where =
    state.channel === "wholesale" ? (
      <>
        {t("terms")} <b>{t("termsValue")}</b>
      </>
    ) : (
      <>
        {t("deliverTo")} <b>{state.location ? t("pinSet") : "Phnom Penh"}</b>
      </>
    );

  // the one thing an older customer wants in reach: a person to call
  const phone = state.config?.officePhone ?? null;
  const extra = phone ? (
    <a className="call" href={`tel:${phone.replace(/\s+/g, "")}`}>
      <PhoneRing />
      {t("callUsShort")}
    </a>
  ) : null;

  // trade ordering is rare and needs a code, so it lives at the end of the shelf, not in the header
  const business = !state.config?.wholesaleOpen ? null : state.channel === "wholesale" ? (
    <button type="button" className="linkbtn" onClick={() => actions.setChannel("retail")}>
      {t("switchRetail")}
    </button>
  ) : (
    <button type="button" className="linkbtn" onClick={chooseBusiness}>
      {t("forBusiness")}
    </button>
  );

  return (
    <div className="shell">
      {state.screen === "shop" ? (
        <Header title={t("brand")} where={where} lang={lang} onLang={toggle} extra={extra} />
      ) : null}

      {askCode && state.screen === "shop" ? (
        <div className="panel codeask">
          <label htmlFor="f-code">{t("businessCode")}</label>
          <input
            id="f-code"
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            value={codeDraft}
            onChange={(e) => setCodeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitCode();
            }}
          />
          <p className="note">{t("businessCodeHow")}</p>
          <div className="picker-actions">
            <button type="button" className="btn quiet" onClick={() => setAskCode(false)}>
              {t("cancel")}
            </button>
            <button type="button" className="btn" onClick={submitCode} disabled={!codeDraft.trim()}>
              {t("enter")}
            </button>
          </div>
        </div>
      ) : null}

      {offline ? <div className="banner bad">{t("offline")}</div> : null}
      {state.error ? (
        <div className="banner bad">
          {state.error === "shop" ? t("shopError") : errorText(t, { code: state.error, status: 0 })}{" "}
          <button
            type="button"
            onClick={() => void actions.refresh()}
            style={{ textDecoration: "underline", fontWeight: 700 }}
          >
            {t("retry")}
          </button>
        </div>
      ) : null}

      {state.screen === "shop" ? (
        <Shop
          state={state}
          t={t}
          lang={lang}
          onBump={actions.bump}
          onOpenCart={() => actions.setScreen("cart")}
          onReorder={actions.reorder}
          foot={
            <>
              <InstallHint t={t} />
              {business}
            </>
          }
        />
      ) : null}

      {state.screen === "cart" ? (
        <Cart
          state={state}
          t={t}
          lang={lang}
          onBump={actions.bump}
          onBack={() => actions.setScreen("shop")}
          onCheckout={() => actions.setScreen("checkout")}
        />
      ) : null}

      {state.screen === "checkout" ? (
        <Checkout
          state={state}
          t={t}
          onBack={() => actions.setScreen("cart")}
          onLocation={actions.setLocation}
          onPlaced={actions.placed}
          onBadCode={() => {
            actions.setWholesaleCode(undefined);
            actions.setChannel("retail");
          }}
        />
      ) : null}

      {state.screen === "track" && state.order ? (
        <Track
          order={state.order}
          t={t}
          lang={lang}
          officePhone={state.config?.officePhone ?? null}
          onAgain={actions.clear}
          onReorder={actions.reorder}
          onCancel={actions.cancelOrder}
        />
      ) : null}
    </div>
  );
}
